// Copyright 2025 - Hewlett Packard Enterprise Company
import { useEffect, useState, useMemo, createContext } from 'react';

import useFlags from '../hooks/useFlags';

import { DARK_MODE_KEY } from '../constants/constants';

export const ThemeModeContext = createContext();

const getModeString = isDarkMode => (isDarkMode ? 'dark' : 'light');
const getModeValue = mode => mode === 'dark';

const getStoredState = () => {
  const storedPreference = localStorage.getItem(DARK_MODE_KEY);

  // use stored preference if present, otherwise default to light
  return storedPreference ? getModeValue(storedPreference) : false;
};

export default function ThemeModeProvider({ children }) {
  const flags = useFlags();
  const isDarkModeEnabled = flags.T_20250602_70791_ENABLE_UI_DARK_MODE_BOOL();
  const [isDarkMode, setIsDarkMode] = useState(getStoredState);

  // when cleaning up T_20250602_70791_ENABLE_UI_DARK_MODE_BOOL, remove this effect
  // because the theme provider is a parent of the auth provider and LD flags targeting
  // specific users evaluate after auth, this is a workaround to ensure the storage is
  // read again to initialize the dark state
  useEffect(() => {
    if (isDarkModeEnabled) {
      setIsDarkMode(getStoredState());
    } else {
      setIsDarkMode(false);
    }
  }, [isDarkModeEnabled]);

  const value = useMemo(
    () => ({
      isDarkMode: isDarkModeEnabled ? isDarkMode : false,
      themeMode: getModeString(isDarkModeEnabled ? isDarkMode : false),
      toggle: () =>
        setIsDarkMode(current => {
          if (!isDarkModeEnabled) return false;

          const nextDarkModeValue = !current;

          localStorage.setItem(DARK_MODE_KEY, getModeString(nextDarkModeValue));

          return nextDarkModeValue;
        }),
    }),
    [isDarkModeEnabled, isDarkMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}
